import { Toaster, toast, TooltipProvider } from '@osteopro/ui';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { I18nProvider } from './i18n/I18nProvider.js';
import { getI18n } from './i18n/config.js';
import { isApiError } from './lib/api.js';
import { AuthProvider } from './lib/auth-provider.js';
import { reportImpersonationEnded } from './lib/auth-store.js';
import { errorMessage } from './lib/error-messages.js';
import { reportSubscriptionRefusal, SUBSCRIPTION_EXPIRED_CODE } from './lib/subscription-block.js';
import { reportApiError } from './lib/suspension-store.js';
import { ThemeProvider } from './lib/theme-provider.js';

/** Codes déjà traités par l'écran qui a lancé la mutation : pas de notification en double. */
const SILENT_CODES = new Set([
  'already_member',
  'appointment_overlap',
  'cabinet_appointment_overlap',
  'duplicate_candidates',
  // La séance « Voir comme » est échue : l'écran revient à la console, sans notification.
  'impersonation_ended',
  'invalid_credentials',
  // Le dialogue des mentions manquantes liste les manques et propose d'émettre quand même.
  'invoice_legal_mentions_missing',
  'last_owner',
  'main_location_required',
  'replacement_appointments_to_reassign',
  'slot_taken',
  // Le cabinet est bloqué : l'écran plein page le dit mieux qu'une notification.
  SUBSCRIPTION_EXPIRED_CODE,
  'totp_required',
]);

function createQueryClient(): QueryClient {
  const client: QueryClient = new QueryClient({
    /*
     * Une suspension de cabinet se lit ici et nulle part ailleurs : elle peut tomber sur
     * n'importe quelle requête, et chaque écran ne peut pas la guetter pour son compte. Le
     * magasin la retient, la coque en tire l'écran plein-page. Même logique pour le blocage
     * d'abonnement, à ceci près que sa source est le cache des droits, qu'on fait relire.
     */
    queryCache: new QueryCache({
      onError: (error) => {
        reportApiError(error);
        reportSubscriptionRefusal(client, error);
        reportImpersonationEnded(error);
      },
    }),
    mutationCache: new MutationCache({
      onError: (error) => {
        reportApiError(error);
        reportSubscriptionRefusal(client, error);
        reportImpersonationEnded(error);
        if (isApiError(error) && SILENT_CODES.has(error.code)) return;
        toast({
          variant: 'destructive',
          title: getI18n().t('common:errors.title'),
          description: errorMessage(error, getI18n().t),
        });
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        refetchOnWindowFocus: false,
        // Les requêtes sont toutes des lectures : une relance est sûre. On ne s'acharne
        // cependant pas sur une réponse que le serveur a déjà tranchée (4xx).
        retry: (failureCount, error) => {
          if (isApiError(error) && error.status < 500) return false;
          return failureCount < 2;
        },
      },
      mutations: { retry: false },
    },
  });
  return client;
}

/** Tous les fournisseurs de l'application, dans l'ordre où ils dépendent les uns des autres. */
export function Providers({ children }: { children: ReactNode }) {
  const [queryClient] = useState(createQueryClient);

  return (
    <QueryClientProvider client={queryClient}>
      <I18nProvider>
        <ThemeProvider>
          <AuthProvider>
            <TooltipProvider>
              {children}
              <Toaster />
            </TooltipProvider>
          </AuthProvider>
        </ThemeProvider>
      </I18nProvider>
    </QueryClientProvider>
  );
}
